import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Upload, CheckCircle2, AlertCircle, Clock, FileText, CreditCard,
  Eye, Heart, TrendingUp, Home, Bell, Settings, LogOut,
  ChevronRight, Calendar, ArrowRight, Lock, Unlock, User, Building2,
  RefreshCw, AlertTriangle, XCircle, Check, X, Camera, Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import BuyerTransactionProfile from '@/components/BuyerTransactionProfile';
import AEDIcon from '@/components/icons/AEDIcon';

// ============ CONSTANTS ============

const KYC_STATUS = {
  NOT_STARTED: 'not_started',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

const KYC_STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-gray-600/20 text-gray-400', icon: AlertCircle },
  submitted: { label: 'Under Review', color: 'bg-gold-600/20 text-gold-400', icon: Clock },
  approved: { label: 'Verified', color: 'bg-green-600/20 text-green-400', icon: CheckCircle2 },
  verified: { label: 'Verified', color: 'bg-green-600/20 text-green-400', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-600/20 text-red-400', icon: XCircle }
};

const COMMITMENT_STATUS = {
  NONE: 'none',
  INITIATED: 'initiated',
  HELD: 'held',
  RELEASED: 'released',
  FORFEITED: 'forfeited'
};

const BUYER_JOURNEY_STEPS = [
  { id: 'register', label: 'Register', icon: User, description: 'Create your account' },
  { id: 'kyc', label: 'KYC', icon: Shield, description: 'Verify your identity' },
  { id: 'commitment', label: 'Commitment', icon: Lock, description: 'Confirm intent' },
  { id: 'viewing', label: 'Viewing', icon: Eye, description: 'Schedule viewings' },
  { id: 'offer', label: 'Offer', icon: AEDIcon, description: 'Make an offer' },
  { id: 'deal', label: 'Deal', icon: CheckCircle2, description: 'Complete transaction' }
];

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// ============ SUB-COMPONENTS ============

const JourneyProgressTracker = ({ currentStep, kycStatus, hasCommitment }) => {
  const getStepStatus = (stepId) => {
    const stepOrder = ['register', 'kyc', 'commitment', 'viewing', 'offer', 'deal'];
    const currentIndex = stepOrder.indexOf(currentStep);
    const stepIndex = stepOrder.indexOf(stepId);
    
    if (stepId === 'register') return 'complete';
    if (stepId === 'kyc') {
      if (kycStatus === 'approved') return 'complete';
      if (kycStatus === 'submitted') return 'pending';
      return currentIndex >= stepIndex ? 'current' : 'locked';
    }
    if (stepId === 'commitment') {
      if (hasCommitment) return 'complete';
      return kycStatus === 'approved' ? 'current' : 'locked';
    }
    
    if (stepIndex < currentIndex) return 'complete';
    if (stepIndex === currentIndex) return 'current';
    return 'locked';
  };

  return (
    <div className="flex items-center justify-between px-4" data-testid="journey-progress">
      {BUYER_JOURNEY_STEPS.map((step, idx) => {
        const status = getStepStatus(step.id);
        const Icon = step.icon;
        
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center transition-all
                ${status === 'complete' ? 'bg-gold-600/20 border-2 border-gold-600 text-green-400' : ''}
                ${status === 'current' ? 'logo-icon-metallic text-primary-foreground' : ''}
                ${status === 'pending' ? 'bg-gold-600/30 text-gold-400 animate-pulse' : ''}
                ${status === 'locked' ? 'bg-white/10 text-muted-foreground' : ''}
              `}>
                {status === 'complete' ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs mt-2 ${status === 'locked' ? 'text-muted-foreground' : status === 'complete' ? 'text-gold-400' : 'text-foreground'}`}>
                {step.label}
              </span>
            </div>
            {idx < BUYER_JOURNEY_STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${
                getStepStatus(BUYER_JOURNEY_STEPS[idx + 1].id) !== 'locked' 
                  ? 'bg-gold-600/50' 
                  : 'bg-white/10'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const KYCPanel = ({ kycStatus, onSubmit, onViewDetails }) => {
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState({
    emirates_id_front: null,
    emirates_id_back: null,
    passport: null,
    proof_of_address: null
  });
  const [previews, setPreviews] = useState({});
  const [showPassport, setShowPassport] = useState(false);
  const [showProofOfAddress, setShowProofOfAddress] = useState(false);

  const handleFileChange = (field, file) => {
    if (file) {
      setDocuments(prev => ({ ...prev, [field]: file }));
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!documents.emirates_id_front || !documents.emirates_id_back) {
      toast.error('Emirates ID (front & back) is required');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      Object.entries(documents).forEach(([key, file]) => {
        if (file) formData.append(key, file);
      });

      await api.post('/kyc/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('KYC documents submitted for review');
      onSubmit?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit KYC documents');
    } finally {
      setUploading(false);
    }
  };

  if (kycStatus === 'approved' || kycStatus === 'verified') {
    return (
      <Card className="border-green-600/30 bg-green-950/10">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-600/20 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h3 className="font-medium text-green-400">KYC Verified</h3>
              <p className="text-sm text-muted-foreground">Your identity has been verified. You have full platform access.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (kycStatus === 'submitted') {
    return (
      <Card className="border-gold-600/30 bg-gold-950/10">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gold-600/20 flex items-center justify-center">
              <Clock className="w-6 h-6 text-gold-400 animate-pulse" />
            </div>
            <div>
              <h3 className="font-medium text-gold-400">Under Review</h3>
              <p className="text-sm text-muted-foreground">Your documents are being reviewed. This typically takes 24-48 hours.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (kycStatus === 'rejected') {
    return (
      <Card className="border-red-600/30 bg-red-950/10">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-red-600/20 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-red-400">Verification Failed</h3>
              <p className="text-sm text-muted-foreground">Please resubmit your documents with clearer images.</p>
            </div>
            <Button onClick={() => onViewDetails?.()} variant="outline" size="sm">
              Resubmit
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // NOT_STARTED - Show upload form
  return (
    <Card className="border-white/10 bg-card/50" data-testid="kyc-upload-form">
      <CardHeader>
        <CardTitle className="font-display text-xl flex items-center gap-2">
          <Shield className="w-5 h-5 text-gold-400" />
          Complete Your KYC
        </CardTitle>
        <CardDescription>
          Verify your identity to unlock full platform access
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Emirates ID - Required */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Emirates ID <span className="text-red-400">*</span></Label>
            <Badge variant="outline" className="text-xs">Required</Badge>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Front */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Front Side</Label>
              <div 
                className={`
                  border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
                  transition-all hover:border-gold-600/50
                  ${previews.emirates_id_front ? 'border-green-600/50' : 'border-white/20'}
                `}
                onClick={() => document.getElementById('emirates_id_front')?.click()}
              >
                {previews.emirates_id_front ? (
                  <img src={previews.emirates_id_front} alt="Front" className="w-full h-24 object-cover rounded" />
                ) : (
                  <>
                    <Camera className="w-8 h-8 mx-auto text-muted-foreground" />
                    <p className="text-xs text-muted-foreground mt-2">Upload Front</p>
                  </>
                )}
                <input
                  id="emirates_id_front"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange('emirates_id_front', e.target.files?.[0])}
                />
              </div>
            </div>
            
            {/* Back */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Back Side</Label>
              <div 
                className={`
                  border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
                  transition-all hover:border-gold-600/50
                  ${previews.emirates_id_back ? 'border-green-600/50' : 'border-white/20'}
                `}
                onClick={() => document.getElementById('emirates_id_back')?.click()}
              >
                {previews.emirates_id_back ? (
                  <img src={previews.emirates_id_back} alt="Back" className="w-full h-24 object-cover rounded" />
                ) : (
                  <>
                    <Camera className="w-8 h-8 mx-auto text-muted-foreground" />
                    <p className="text-xs text-muted-foreground mt-2">Upload Back</p>
                  </>
                )}
                <input
                  id="emirates_id_back"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange('emirates_id_back', e.target.files?.[0])}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Passport - Optional Toggle */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox 
              id="show_passport" 
              checked={showPassport}
              onCheckedChange={setShowPassport}
            />
            <Label htmlFor="show_passport" className="text-sm cursor-pointer">
              Add Passport (Optional)
            </Label>
          </div>
          
          {showPassport && (
            <div 
              className={`
                border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
                transition-all hover:border-gold-600/50
                ${previews.passport ? 'border-green-600/50' : 'border-white/20'}
              `}
              onClick={() => document.getElementById('passport')?.click()}
            >
              {previews.passport ? (
                <img src={previews.passport} alt="Passport" className="w-full h-32 object-cover rounded" />
              ) : (
                <>
                  <FileText className="w-8 h-8 mx-auto text-muted-foreground" />
                  <p className="text-xs text-muted-foreground mt-2">Upload Passport Photo Page</p>
                </>
              )}
              <input
                id="passport"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange('passport', e.target.files?.[0])}
              />
            </div>
          )}
        </div>

        {/* Proof of Address - Optional Toggle */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox 
              id="show_poa" 
              checked={showProofOfAddress}
              onCheckedChange={setShowProofOfAddress}
            />
            <Label htmlFor="show_poa" className="text-sm cursor-pointer">
              Add Proof of Address (Optional)
            </Label>
          </div>
          
          {showProofOfAddress && (
            <div 
              className={`
                border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
                transition-all hover:border-gold-600/50
                ${previews.proof_of_address ? 'border-green-600/50' : 'border-white/20'}
              `}
              onClick={() => document.getElementById('proof_of_address')?.click()}
            >
              {previews.proof_of_address ? (
                <img src={previews.proof_of_address} alt="POA" className="w-full h-32 object-cover rounded" />
              ) : (
                <>
                  <Home className="w-8 h-8 mx-auto text-muted-foreground" />
                  <p className="text-xs text-muted-foreground mt-2">Upload Utility Bill / Bank Statement</p>
                </>
              )}
              <input
                id="proof_of_address"
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => handleFileChange('proof_of_address', e.target.files?.[0])}
              />
            </div>
          )}
        </div>

        <Button 
          onClick={handleSubmit} 
          disabled={uploading || !documents.emirates_id_front || !documents.emirates_id_back}
          className="w-full btn-gold-metallic"
          data-testid="submit-kyc-btn"
        >
          {uploading ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> Submit for Verification</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

const CommitmentPanel = ({ hasCommitment, commitmentData, onInitiate, kycApproved, userId }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [commitmentAmount, setCommitmentAmount] = useState(10);
  const [processing, setProcessing] = useState(false);

  const handleInitiateCommitment = async () => {
    if (!acknowledged) {
      toast.error('Please acknowledge the commitment terms');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/buyer/commitment/initiate?user_id=${userId}`, {
        commitment_percentage: commitmentAmount,
        acknowledged: true
      });
      
      toast.success('Commitment initiated! Confirming your intent...');
      
      // Auto-transition to HELD state for a smoother UX
      // In production, this might require additional verification
      setShowDialog(false);
      onInitiate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate commitment');
    } finally {
      setProcessing(false);
    }
  };

  if (!kycApproved) {
    return (
      <Card className="border-white/10 bg-white/5 opacity-60">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-medium text-muted-foreground">Intent Commitment</h3>
              <p className="text-sm text-muted-foreground">Complete KYC verification first to unlock this step.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (hasCommitment) {
    const isHeld = commitmentData?.status === 'held';
    const commitmentPct = commitmentData?.commitment_percentage || 10;
    const commitmentAmt = commitmentData?.commitment_amount;
    
    return (
      <Card className="border-green-600/30 bg-green-950/10">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-600/20 flex items-center justify-center">
              {isHeld ? <Unlock className="w-6 h-6 text-green-400" /> : <Clock className="w-6 h-6 text-gold-400" />}
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-green-400">
                {isHeld ? 'Commitment Active' : 'Commitment Pending'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {commitmentPct}% deposit intent {isHeld ? 'confirmed' : 'initiated'}.
                {commitmentAmt && ` (AED ${commitmentAmt.toLocaleString()})`}
                {isHeld && ' You can now request viewings and make offers.'}
              </p>
              {commitmentData?.property_title && (
                <p className="text-xs text-muted-foreground mt-1">
                  Property: {commitmentData.property_title}
                </p>
              )}
            </div>
            <Badge className={isHeld ? "bg-green-600/20 text-green-400 border-green-600/30" : "bg-gold-600/20 text-gold-400 border-gold-600/30"}>
              {(commitmentData?.status || 'HELD').toUpperCase()}
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-gold-600/30 bg-gold-950/10" data-testid="commitment-panel">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full logo-icon-metallic flex items-center justify-center">
              <Lock className="w-6 h-6 text-primary-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium gold-interactive">Confirm Your Intent</h3>
              <p className="text-sm text-muted-foreground">
                To request viewings and make offers, confirm your purchase intent.
              </p>
            </div>
            <Button 
              onClick={() => setShowDialog(true)}
              className="btn-gold-metallic"
              data-testid="initiate-commitment-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Confirm Intent
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-gold-400" />
              Intent Commitment
            </DialogTitle>
            <DialogDescription>
              Confirm your serious intent to purchase. This is required before requesting viewings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="p-4 rounded bg-gold-950/20 border border-gold-600/30">
              <h4 className="font-medium text-gold-400 mb-2">What this means:</h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-gold-400 mt-0.5 shrink-0" />
                  <span>You're confirming serious purchase intent</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-gold-400 mt-0.5 shrink-0" />
                  <span>Standard deposit is 10% of property value (adjustable)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-gold-400 mt-0.5 shrink-0" />
                  <span>No payment required now - this records your intent</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-gold-400 mt-0.5 shrink-0" />
                  <span>Deposit terms apply at Form F stage</span>
                </li>
              </ul>
            </div>

            <div>
              <Label className="text-sm">Commitment Percentage</Label>
              <div className="flex items-center gap-4 mt-2">
                <Input
                  type="number"
                  value={commitmentAmount}
                  onChange={(e) => setCommitmentAmount(Math.min(100, Math.max(5, parseInt(e.target.value) || 10)))}
                  className="w-24 bg-white/5 border-white/10"
                  min={5}
                  max={100}
                />
                <span className="text-muted-foreground">% of property value</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Standard: 10%. Range: 5-100%</p>
            </div>

            <div className="flex items-start gap-3 p-3 rounded bg-white/5 border border-white/10">
              <Checkbox 
                id="acknowledge" 
                checked={acknowledged}
                onCheckedChange={setAcknowledged}
              />
              <Label htmlFor="acknowledge" className="text-sm cursor-pointer leading-relaxed">
                I understand that this commitment records my serious intent to purchase. 
                I acknowledge the deposit terms that will apply at the Form F stage of any transaction.
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleInitiateCommitment}
              disabled={!acknowledged || processing}
              className="btn-gold-metallic"
              data-testid="confirm-commitment-btn"
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Confirm Commitment</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ShortlistTab = ({ shortlist, onViewProperty, onRequestViewing, canRequestViewing, userId }) => {
  const [viewingDialog, setViewingDialog] = useState({ open: false, propertyId: null, property: null });
  const [viewingRequest, setViewingRequest] = useState({
    preferred_dates: [],
    preferred_times: ['morning', 'afternoon'],
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleOpenViewingDialog = (propertyId, property) => {
    if (!canRequestViewing) {
      toast.error('Complete KYC and Intent Commitment to request viewings');
      return;
    }
    setViewingDialog({ open: true, propertyId, property });
  };

  const handleAddDate = () => {
    if (selectedDate && viewingRequest.preferred_dates.length < 3) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      if (!viewingRequest.preferred_dates.includes(dateStr)) {
        setViewingRequest(prev => ({
          ...prev,
          preferred_dates: [...prev.preferred_dates, dateStr]
        }));
        setSelectedDate(null);
      }
    }
  };

  const handleRemoveDate = (date) => {
    setViewingRequest(prev => ({
      ...prev,
      preferred_dates: prev.preferred_dates.filter(d => d !== date)
    }));
  };

  const handleToggleTime = (time) => {
    setViewingRequest(prev => ({
      ...prev,
      preferred_times: prev.preferred_times.includes(time)
        ? prev.preferred_times.filter(t => t !== time)
        : [...prev.preferred_times, time]
    }));
  };

  const handleSubmitViewingRequest = async () => {
    if (viewingRequest.preferred_dates.length === 0) {
      toast.error('Please select at least one preferred date');
      return;
    }
    if (viewingRequest.preferred_times.length === 0) {
      toast.error('Please select at least one preferred time');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/buyer/viewings/request?user_id=${userId}`, {
        property_id: viewingDialog.propertyId,
        preferred_dates: viewingRequest.preferred_dates,
        preferred_times: viewingRequest.preferred_times,
        notes: viewingRequest.notes
      });
      
      toast.success('Viewing request submitted! The seller will respond shortly.');
      setViewingDialog({ open: false, propertyId: null, property: null });
      setViewingRequest({ preferred_dates: [], preferred_times: ['morning', 'afternoon'], notes: '' });
      onRequestViewing?.(viewingDialog.propertyId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request viewing');
    } finally {
      setSubmitting(false);
    }
  };

  if (shortlist.length === 0) {
    return (
      <div className="text-center py-12">
        <Heart className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium">No Properties Shortlisted</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Browse properties and add them to your shortlist to track them here.
        </p>
        <Button className="mt-4 btn-gold-metallic" onClick={() => window.location.href = '/properties'}>
          <Building2 className="w-4 h-4 mr-2" />
          Browse Properties
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shortlist.map((item) => (
          <Card key={item.id} className="border-white/10 hover:border-gold-600/30 transition-all">
            <div className="aspect-video relative overflow-hidden rounded-t-lg">
              {item.property?.images?.[0] ? (
                <img 
                  src={item.property.images[0]} 
                  alt={item.property.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-white/10 flex items-center justify-center">
                  <Building2 className="w-12 h-12 text-muted-foreground/30" />
                </div>
              )}
              <Badge className="absolute top-2 right-2 bg-gold-600/80 text-primary-foreground">
                {item.stage?.replace(/_/g, ' ')}
              </Badge>
            </div>
            <CardContent className="p-4">
              <h4 className="font-medium truncate">{item.property?.title || 'Property'}</h4>
              <p className="text-sm text-muted-foreground">{item.property?.location?.area}</p>
              <p className="text-lg font-display gold-interactive mt-2">
                {formatCurrency(item.property?.price)}
              </p>
              <div className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => onViewProperty(item.property_id)}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  View
                </Button>
                {canRequestViewing && item.stage !== 'viewed' && (
                  <Button 
                    size="sm" 
                    className="flex-1 btn-gold-metallic"
                    onClick={() => handleOpenViewingDialog(item.property_id, item.property)}
                    data-testid={`request-viewing-${item.property_id}`}
                  >
                    <Calendar className="w-3 h-3 mr-1" />
                    Request Viewing
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Viewing Request Dialog */}
      <Dialog open={viewingDialog.open} onOpenChange={(open) => !open && setViewingDialog({ open: false, propertyId: null, property: null })}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gold-400" />
              Request Property Viewing
            </DialogTitle>
            <DialogDescription>
              {viewingDialog.property?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-sm">Preferred Dates (select up to 3)</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="date"
                  value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : null)}
                  min={new Date().toISOString().split('T')[0]}
                  className="flex-1 bg-white/5 border-white/10"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleAddDate}
                  disabled={!selectedDate || viewingRequest.preferred_dates.length >= 3}
                >
                  Add
                </Button>
              </div>
              {viewingRequest.preferred_dates.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {viewingRequest.preferred_dates.map(date => (
                    <Badge key={date} className="bg-gold-600/20 text-gold-400 border-gold-600/30">
                      {new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      <button 
                        className="ml-2 hover:text-red-400"
                        onClick={() => handleRemoveDate(date)}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm">Preferred Time Slots</Label>
              <div className="flex gap-2 mt-2">
                {['morning', 'afternoon', 'evening'].map(time => (
                  <Button
                    key={time}
                    type="button"
                    variant={viewingRequest.preferred_times.includes(time) ? 'default' : 'outline'}
                    className={viewingRequest.preferred_times.includes(time) ? 'btn-gold-metallic' : ''}
                    onClick={() => handleToggleTime(time)}
                    size="sm"
                  >
                    {time.charAt(0).toUpperCase() + time.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Input
                placeholder="Any specific requirements or questions..."
                value={viewingRequest.notes}
                onChange={(e) => setViewingRequest(prev => ({ ...prev, notes: e.target.value }))}
                className="mt-2 bg-white/5 border-white/10"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingDialog({ open: false, propertyId: null, property: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitViewingRequest}
              disabled={submitting || viewingRequest.preferred_dates.length === 0}
              className="btn-gold-metallic"
              data-testid="submit-viewing-request-btn"
            >
              {submitting ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Calendar className="w-4 h-4 mr-2" /> Submit Request</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ViewingsTab = ({ viewings, onViewDetails, onConfirm, onCancel }) => {
  const STATUS_CONFIG = {
    pending: { color: 'bg-gold-600/20 text-gold-400', label: 'Pending' },
    approved: { color: 'bg-blue-600/20 text-blue-400', label: 'Approved' },
    confirmed: { color: 'bg-green-600/20 text-green-400', label: 'Confirmed' },
    completed: { color: 'bg-green-600/20 text-green-400', label: 'Completed' },
    cancelled: { color: 'bg-red-600/20 text-red-400', label: 'Cancelled' },
    rejected: { color: 'bg-red-600/20 text-red-400', label: 'Rejected' }
  };

  if (viewings.length === 0) {
    return (
      <div className="text-center py-12">
        <Eye className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium">No Viewings Scheduled</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Request viewings from your shortlisted properties.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {viewings.map((viewing) => {
        const config = STATUS_CONFIG[viewing.status] || STATUS_CONFIG.pending;
        
        return (
          <Card key={viewing.id} className="border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded overflow-hidden shrink-0">
                  {viewing.property?.images?.[0] ? (
                    <img 
                      src={viewing.property.images[0]} 
                      alt={viewing.property.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-white/10 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-muted-foreground/30" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium truncate">{viewing.property?.title || 'Property'}</h4>
                    <Badge className={config.color}>{config.label}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{viewing.property?.location?.area}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(viewing.scheduled_date || viewing.preferred_date)}
                    </span>
                    {viewing.scheduled_time && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {viewing.scheduled_time}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {viewing.status === 'approved' && !viewing.confirmed_by_buyer_at && (
                    <Button 
                      size="sm" 
                      className="btn-gold-metallic"
                      onClick={() => onConfirm(viewing.id)}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Confirm
                    </Button>
                  )}
                  {['pending', 'approved'].includes(viewing.status) && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-red-600/50 text-red-400 hover:bg-red-950/30"
                      onClick={() => onCancel(viewing.id)}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Cancel
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onViewDetails(viewing.id)}
                  >
                    Details
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const OffersTab = ({ offers, onViewOffer, onWithdraw }) => {
  const STATUS_CONFIG = {
    submitted: { color: 'bg-gold-600/20 text-gold-400', label: 'Submitted' },
    pending: { color: 'bg-gold-600/20 text-gold-400', label: 'Pending' },
    countered: { color: 'bg-blue-600/20 text-blue-400', label: 'Countered' },
    accepted: { color: 'bg-green-600/20 text-green-400', label: 'Accepted' },
    rejected: { color: 'bg-red-600/20 text-red-400', label: 'Rejected' },
    withdrawn: { color: 'bg-gray-600/20 text-gray-400', label: 'Withdrawn' }
  };

  if (offers.length === 0) {
    return (
      <div className="text-center py-12">
        <AEDIcon className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium">No Offers Made</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Complete a viewing to make an offer on a property.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {offers.map((offer) => {
        const config = STATUS_CONFIG[offer.status] || STATUS_CONFIG.pending;
        
        return (
          <Card key={offer.id} className="border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium truncate">{offer.property?.title || 'Property'}</h4>
                    <Badge className={config.color}>{config.label}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{offer.property?.location?.area}</p>
                  
                  <div className="flex items-center gap-6 mt-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Your Offer</p>
                      <p className="font-display text-lg gold-interactive">{formatCurrency(offer.amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Asking Price</p>
                      <p className="font-display text-lg">{formatCurrency(offer.property?.price)}</p>
                    </div>
                    {offer.counter_amount && (
                      <div>
                        <p className="text-xs text-muted-foreground">Counter Offer</p>
                        <p className="font-display text-lg text-blue-400">{formatCurrency(offer.counter_amount)}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {offer.status === 'countered' && (
                    <Button 
                      size="sm" 
                      className="btn-gold-metallic"
                      onClick={() => onViewOffer(offer.id)}
                    >
                      Respond
                    </Button>
                  )}
                  {['submitted', 'pending'].includes(offer.status) && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-red-600/50 text-red-400 hover:bg-red-950/30"
                      onClick={() => onWithdraw(offer.id)}
                    >
                      Withdraw
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onViewOffer(offer.id)}
                  >
                    Details
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const DealsTab = ({ deals, onViewDeal }) => {
  if (deals.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium">No Active Deals</h3>
        <p className="text-sm text-muted-foreground mt-2">
          When your offer is accepted, your deal will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {deals.map((deal) => (
        <Card key={deal.id} className="border-white/10 hover:border-gold-600/30 transition-all cursor-pointer" onClick={() => onViewDeal(deal.id)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">{deal.property_title || 'Property Deal'}</h4>
                <p className="text-sm text-muted-foreground font-mono">{deal.id?.slice(0, 8).toUpperCase()}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Deal Value</p>
                  <p className="font-display gold-interactive">{formatCurrency(deal.agreed_price)}</p>
                </div>
                <Badge className="bg-green-600/20 text-green-400 border-green-600/30">
                  {deal.deal_state?.replace(/_/g, ' ')}
                </Badge>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============ MAIN COMPONENT ============

export default function BuyerDashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [buyerProfile, setBuyerProfile] = useState(null);
  const [shortlist, setShortlist] = useState([]);
  const [viewings, setViewings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [commitment, setCommitment] = useState(null);

  const kycStatus = buyerProfile?.kyc_status || 'not_started';
  const isKycApproved = kycStatus === 'approved' || kycStatus === 'verified';
  const hasCommitment = commitment?.status === 'held' || commitment?.status === 'initiated';
  const canRequestViewing = isKycApproved && (commitment?.status === 'held');
  const canMakeOffer = canRequestViewing;

  const fetchData = useCallback(async () => {
    try {
      const userId = user?.id;
      const [profileRes, shortlistRes, viewingsRes, offersRes, dealsRes, commitmentRes] = await Promise.allSettled([
        api.get('/auth/me'),
        api.get('/interests/my-shortlist'),
        api.get(`/buyer/viewings${userId ? `?user_id=${userId}` : ''}`),
        api.get('/offers/buyer/my-offers'),
        api.get('/deals'),
        api.get(`/buyer/commitment${userId ? `?user_id=${userId}` : ''}`)
      ]);

      if (profileRes.status === 'fulfilled') setBuyerProfile(profileRes.value.data);
      if (shortlistRes.status === 'fulfilled') {
        const shortlistData = shortlistRes.value.data;
        setShortlist(Array.isArray(shortlistData) ? shortlistData : shortlistData?.items || []);
      }
      if (viewingsRes.status === 'fulfilled') {
        const viewingsData = viewingsRes.value.data;
        setViewings(Array.isArray(viewingsData) ? viewingsData : viewingsData?.viewings || []);
      }
      if (offersRes.status === 'fulfilled') {
        const offersData = offersRes.value.data;
        setOffers(Array.isArray(offersData) ? offersData : offersData?.offers || []);
      }
      if (dealsRes.status === 'fulfilled') {
        const dealsData = dealsRes.value.data;
        setDeals(Array.isArray(dealsData) ? dealsData : dealsData?.deals || []);
      }
      if (commitmentRes.status === 'fulfilled') setCommitment(commitmentRes.value.data);
      
    } catch (error) {
      console.error('Failed to fetch buyer data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCurrentStep = () => {
    if (deals.length > 0) return 'deal';
    if (offers.some(o => ['submitted', 'countered', 'pending'].includes(o.status))) return 'offer';
    if (viewings.some(v => ['pending', 'approved', 'confirmed'].includes(v.status))) return 'viewing';
    if (hasCommitment) return 'commitment';
    if (kycStatus === 'approved') return 'kyc';
    return 'register';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleRequestViewing = async (propertyId) => {
    if (!canRequestViewing) {
      toast.error('Complete KYC and Intent Commitment to request viewings');
      setActiveTab('overview');
      return;
    }
    navigate(`/properties/${propertyId}?action=viewing`);
  };

  const handleConfirmViewing = async (viewingId) => {
    try {
      await api.post(`/viewings/${viewingId}/buyer-confirm`);
      toast.success('Viewing confirmed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm viewing');
    }
  };

  const handleCancelViewing = async (viewingId) => {
    try {
      await api.post(`/viewings/${viewingId}/cancel`);
      toast.success('Viewing cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel viewing');
    }
  };

  const handleWithdrawOffer = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/withdraw`);
      toast.success('Offer withdrawn');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw offer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-gold-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="buyer-dashboard-page">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Rashii Logo - Consistent sizing */}
              <div 
                className="flex items-center gap-2 cursor-pointer group"
                onClick={() => navigate('/')}
              >
                <div className="w-10 h-10 rounded logo-icon-metallic flex items-center justify-center">
                  <span className="font-display text-lg text-primary-foreground font-bold">R</span>
                </div>
                <span className="font-display text-xl font-medium tracking-tight gold-interactive">Rashii</span>
              </div>
              <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30">
                BUYER
              </Badge>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => toast.info('Notifications coming soon')}
                className="relative"
              >
                <Bell className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/settings')}
              >
                <Settings className="w-5 h-5" />
              </Button>
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-6">
        {/* Journey Progress */}
        <Card className="border-white/10 bg-card/50 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Your Journey</CardTitle>
          </CardHeader>
          <CardContent>
            <JourneyProgressTracker 
              currentStep={getCurrentStep()} 
              kycStatus={kycStatus}
              hasCommitment={hasCommitment}
            />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10 mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="shortlist">Shortlist ({shortlist.length})</TabsTrigger>
            <TabsTrigger value="viewings">Viewings ({viewings.length})</TabsTrigger>
            <TabsTrigger value="offers">Offers ({offers.length})</TabsTrigger>
            <TabsTrigger value="deals">Deals ({deals.length})</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="glass-card border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Shortlisted</p>
                      <p className="text-3xl font-display gold-interactive mt-1">{shortlist.length}/3</p>
                    </div>
                    <Heart className="w-8 h-8 text-gold-400/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-card border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Viewings</p>
                      <p className="text-3xl font-display gold-interactive mt-1">{viewings.filter(v => v.status !== 'cancelled').length}</p>
                    </div>
                    <Eye className="w-8 h-8 text-gold-400/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-card border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Active Offers</p>
                      <p className="text-3xl font-display gold-interactive mt-1">{offers.filter(o => ['submitted', 'pending', 'countered'].includes(o.status)).length}</p>
                    </div>
                    <AEDIcon className="w-8 h-8 text-gold-400/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-card border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Active Deals</p>
                      <p className="text-3xl font-display gold-interactive mt-1">{deals.length}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-gold-400/50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Transaction Profile - Soft Visibility */}
            <BuyerTransactionProfile 
              token={localStorage.getItem('rashii_access_token')} 
              buyerId={user?.id} 
            />

            {/* KYC Panel */}
            <KYCPanel 
              kycStatus={kycStatus}
              onSubmit={fetchData}
              onViewDetails={() => setActiveTab('settings')}
            />

            {/* Commitment Panel */}
            <CommitmentPanel
              hasCommitment={hasCommitment}
              commitmentData={commitment}
              onInitiate={fetchData}
              kycApproved={isKycApproved}
              userId={user?.id}
            />

            {/* Quick Actions */}
            <Card className="border-white/10 bg-card/50">
              <CardHeader>
                <CardTitle className="font-display text-xl">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex-col border-white/10 hover:border-gold-600/30"
                    onClick={() => navigate('/properties')}
                  >
                    <Building2 className="w-6 h-6 mb-2 text-gold-400" />
                    <span>Browse Properties</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex-col border-white/10 hover:border-gold-600/30"
                    onClick={() => setActiveTab('shortlist')}
                  >
                    <Heart className="w-6 h-6 mb-2 text-gold-400" />
                    <span>My Shortlist</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex-col border-white/10 hover:border-gold-600/30"
                    onClick={() => navigate('/viewings')}
                  >
                    <Calendar className="w-6 h-6 mb-2 text-gold-400" />
                    <span>My Viewings</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex-col border-white/10 hover:border-gold-600/30"
                    onClick={() => navigate('/deals')}
                  >
                    <FileText className="w-6 h-6 mb-2 text-gold-400" />
                    <span>My Deals</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shortlist Tab */}
          <TabsContent value="shortlist">
            <Card className="border-white/10 bg-card/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display text-xl">Your Shortlist</CardTitle>
                  <Badge variant="outline">{shortlist.length}/3 slots used</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ShortlistTab 
                  shortlist={shortlist}
                  onViewProperty={(id) => navigate(`/properties/${id}`)}
                  onRequestViewing={fetchData}
                  canRequestViewing={canRequestViewing}
                  userId={user?.id}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Viewings Tab */}
          <TabsContent value="viewings">
            <Card className="border-white/10 bg-card/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display text-xl">Your Viewings</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => navigate('/viewings')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ViewingsTab 
                  viewings={viewings}
                  onViewDetails={(id) => navigate(`/viewings/${id}`)}
                  onConfirm={handleConfirmViewing}
                  onCancel={handleCancelViewing}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <Card className="border-white/10 bg-card/50">
              <CardHeader>
                <CardTitle className="font-display text-xl">Your Offers</CardTitle>
              </CardHeader>
              <CardContent>
                <OffersTab 
                  offers={offers}
                  onViewOffer={(id) => navigate(`/offers/${id}`)}
                  onWithdraw={handleWithdrawOffer}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deals Tab */}
          <TabsContent value="deals">
            <Card className="border-white/10 bg-card/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display text-xl">Your Deals</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => navigate('/deals')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <DealsTab 
                  deals={deals}
                  onViewDeal={(id) => navigate(`/deals/${id}`)}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
